import { inject, injectable } from "inversify";
import { ApiData, ApiPage } from "@/core/models/page.model";
import { TableRepo } from "../table.repo";
import { ContractModel, DepositSummaryModel } from "@/core/models/contracts/contract.model";
import { TollAreaRepo } from "./toll-area.repo";
import { UnitTypeRepo } from "./unit-type.repo";

interface _ContractGetParams {
    search?: string
    provinceId?: string
    districtId?: string
    wardId?: string
    tollAreaId?: string
    meterCode?: string
    phone?: string
    identityNo?: string
    hasMeter?: boolean
    depositStatus?: string
    note?: string
    status?: string
}

interface GenerateCodePayload {
    code: string,
    identityCode: number
}

@injectable()
export class ContractRepo extends TableRepo<ContractModel> {
    @inject(TollAreaRepo)
    private tollAreaRepo!: TollAreaRepo;

    @inject(UnitTypeRepo)
    private unitTypeRepo!: UnitTypeRepo;

    constructor() {
        super("/api/WaterUser");
    }

    async getData(index: number, params: _ContractGetParams, pageSize = 10): Promise<ApiPage<ContractModel>|null> {
        const result = await super.getData(index, params, pageSize);
        const tollAreas = await this.tollAreaRepo.getAll();
        const unitTypes = await this.unitTypeRepo.getAll();
        if (result) {
            const data: ContractModel[] = [];
            for (const record of result.data) {
                const populatedRecord: ContractModel = {
                    ...record,
                    tollAreaName: tollAreas.find(area => area.id === record.tollAreaId)?.name,
                    unitTypeName: unitTypes.find(area => area.id === record.unitTypeId)?.name,
                };
                data.push(populatedRecord);
            }
            result.data = data;
            return result;
        }
        return null;
    }

    
    async exportExcel(params: object) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-excel-all`, params)
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async getSummary(params: object): Promise<DepositSummaryModel[]> {
        try {
            const response = await this.apiRepo.get<ApiData<DepositSummaryModel[]>>(`${this.basepath}/summary-by-time`, params);
            if (response?.code === '00') {
                return response.result.data
            };
        } catch (err) {
            console.log(err);
        }
        return [];
    }

    async generateCode(tollAreaId: string) {
        try {
            const response = await this.apiRepo.get<GenerateCodePayload>(`${this.basepath}/new-code`, {tollAreaId});
            if (response?.code === '00') {
                return response.result
            };
            return null;
        } catch (err) {
            console.log(err);
        }
    }

    async exportContract(contractId: string) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-contract`, {waterUserId: contractId})
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async exportContractByTollArea(tollAreaId: string) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-contract-by-required`, {tollAreaId})
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async exportAggreement(contractId: string) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-agreement`, {waterUserId: contractId})
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async getAllIds(params: _ContractGetParams): Promise<string[]> {
        try {
            // Fetch first page to get total
            const firstPage = await this.getData(1, params, 10);
            if (!firstPage || firstPage.total === 0) {
                return [];
            }

            // Fetch all data with large page size
            const allData = await this.getData(1, params, firstPage.total);
            if (allData) {
                return allData.data.map(item => item.id);
            }
            return [];
        } catch (err) {
            console.log(err);
            return [];
        }
    }
}